import { useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { sharedRequestsTyped } from "@/api/(requests)/sharedTyped";
import { Tooltip2 } from "@/components/UI2/molecules/Tooltip";
import type { UUID } from "@/core/types";

import { AddendumDisplay } from "./components/AddendumDisplay";
import { AddendumForm } from "./components/AddendumForm";

type NoteAddendumProps = {
  credentials: string | undefined;
  data: string | undefined;
  isDisabled?: boolean;
  noteId: UUID;
  onSubmit: () => void;
  patientId: UUID;
  signature: string | undefined;
  textTestId: string | undefined;
  timestamp: number | undefined;
};

export function NoteAddendum({
  credentials,
  data,
  isDisabled = false,
  noteId,
  onSubmit,
  patientId,
  signature,
  textTestId,
  timestamp,
}: NoteAddendumProps) {
  const [isEditMode, setIsEditMode] = useState(false);

  const isNewCard = !data;
  const canAddNew = isNewCard && !isEditMode;
  const isEditing = isNewCard && isEditMode;

  const submitAddendum = async (addendum: string) => {
    const urlParams = { patientId };
    const body = {
      addendum,
      credentials,
      note_id: noteId,
      signature,
      timestamp: dayjs().unix(),
    };

    await sharedRequestsTyped.create_note_addendum({ body, urlParams });
    onSubmit();
  };

  return (
    <DashboardCard2
      Actions={
        <>
          {canAddNew && (
            <AddNewButton isDisabled={isDisabled} onPress={() => setIsEditMode(true)} />
          )}

          {!isNewCard && (
            <time dateTime={dayjs.unix(timestamp!).toISOString()}>
              {dayjs.unix(timestamp!).format("MM/DD/YYYY hh:mma")}
            </time>
          )}
        </>
      }
      className={cn(canAddNew && "tw:pb-0!")}
      classNameChildren="tw:flex tw:w-full"
      classNameHeader={cn(canAddNew && "tw:mb-0!")}
      rawChildren
      title="Addendum"
    >
      {isEditing && (
        <AddendumForm
          onCancel={() => setIsEditMode(false)}
          onSubmit={async (formData) => {
            await submitAddendum(formData.addendum);
            setIsEditMode(false);
          }}
        />
      )}

      {!isNewCard && (
        <AddendumDisplay
          credentials={credentials}
          data={data}
          signature={signature}
          textTestId={textTestId}
        />
      )}
    </DashboardCard2>
  );
}

type AddNewButtonProps = {
  isDisabled: boolean;
  onPress: () => void;
};

function AddNewButton({ isDisabled, onPress }: AddNewButtonProps) {
  const button = (
    <Button2
      isDisabled={isDisabled}
      onPress={onPress}
      variant="link"
      {...getAutomationDataAttr("note-addendum-link")}
    >
      Add new
    </Button2>
  );

  if (!isDisabled) return button;

  return (
    <Tooltip2
      className="tw:min-w-70"
      text="Addendums are temporarily unavailable for accounts requiring co-signatures. This feature will be fully restored in our next scheduled update."
    >
      {button}
    </Tooltip2>
  );
}
